import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { GripVertical } from "lucide-react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useRequireAuth } from "../../hooks/useRequireAuth";
export default function NavigationEditor({ venue: propVenue }) {
  useRequireAuth()
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { name } = useParams();
  const venue = propVenue || name;

  // Stan do obsługi drag & drop
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (!venue) return;

    const load = async () => {
      const snap = await getDocs(collection(db, "venues", venue, "pages"));
      const list = snap.docs
        .map((d) => ({
          id: d.id,
          title: d.data().title || d.id,
          order: d.data().order ?? 999,
          slug: d.data().slug || "",
        }))
        .sort((a, b) => a.order - b.order);

      setPages(list);
      setLoading(false);
    };

    load();
  }, [venue]);

  // Przesuwanie strzałkami (alternatywa)
  const move = (index, dir) => {
    const copy = [...pages];
    const target = index + dir;
    if (target < 0 || target >= copy.length) return;

    [copy[index], copy[target]] = [copy[target], copy[index]];
    setPages(copy.map((p, i) => ({ ...p, order: i })));
  };

  // Drag & Drop handlers
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const copy = [...pages];
    const draggedItem = copy[dragIndex];
    copy.splice(dragIndex, 1);
    copy.splice(dropIndex, 0, draggedItem);

    setPages(copy.map((p, i) => ({ ...p, order: i })));
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const save = async () => {
    setSaving(true);
    await Promise.all(
      pages.map((p, i) =>
        updateDoc(doc(db, "venues", venue, "pages", p.id), { order: i })
      )
    );
    setSaving(false);
  };

  if (loading) {
    return <div className="mt-20 text-center">Ładowanie stron...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto mt-20 space-y-8">
      <h2 className="text-2xl font-semibold text-center">Edytor kolejności menu</h2>

      {/* Podgląd nawigacji */}
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:block border-b">
          <div className="flex items-center justify-center px-8 py-4 gap-12">
            <ul className="flex items-center gap-10 font-medium text-lg">
              {pages.map((page, i) => (
                <li
                  key={page.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 py-2 group cursor-move transition-all ${
                    dragIndex === i ? "opacity-50 scale-105" : ""
                  } ${dragOverIndex === i ? "scale-105" : ""}`}
                >
                  <GripVertical className="text-gray-500" />
                  <span className="text-gray-700">{page.title}</span>

                  {/* Strzałki jako alternatywa */}
                  <div className="flex flex-col ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => move(i, -1)}
                      className="text-xs text-gray-500 hover:text-gray-900"
                      aria-label="W górę"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => move(i, 1)}
                      className="text-xs text-gray-500 hover:text-gray-900"
                      aria-label="W dół"
                    >
                      ↓
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          <div className="px-6 pt-20 pb-8 bg-white min-h-screen">
            <ul className="flex flex-col items-center gap-8 font-medium text-3xl">
              {pages.map((page, i) => (
                <li
                  key={page.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-6 group cursor-move transition-all ${
                    dragIndex === i ? "opacity-50" : ""
                  }`}
                >
                  <GripVertical className="text-gray-500 text-4xl" />
                  <span>{page.title}</span>

                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} className="text-2xl">↑</button>
                    <button onClick={() => move(i, 1)} className="text-2xl">↓</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-4">
        <p className="text-sm text-gray-600">
          Przeciągaj elementy za uchwyt <GripVertical className="inline w-5 h-5 mx-1" /> aby zmienić kolejność. Możesz też używać strzałek.
        </p>
        <button
          onClick={save}
          disabled={saving || pages.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
        >
          {saving ? "Zapisywanie…" : "Zapisz kolejność menu"}
        </button>
        <Link to={`/${name}/admin`} className="text-emerald-600 hover:text-emerald-800 font-medium">
          Wróć do panelu administracyjnego
        </Link>
      </div>
    </div>
  );
}